import { StrictMode, VFC, useRef, useState } from 'react'
import { toast } from '@opallabs/lapidary'
import { useTextEditor } from '../../context'
import type { AssetAttributes } from '../../extensions/Asset/types'
import { UploadFailedToast, UploadProgressToast } from '../../toasts'
import { MemoizedPopover } from './MemoizedPopover'
import { UploadControlButtonProps } from './types'

export const UploadControlButton: VFC<UploadControlButtonProps> = ({
  label,
  icon,
  styleOverrides,
  allowedFileTypes,
  ...buttonProps
}) => {
  const { editor, uploadAssets } = useTextEditor()
  const fileInput = useRef<HTMLInputElement>(null)
  const labelText =
    typeof label === 'function' ? label({ isActive: false }) : label
  const [uploadTriggered, setUploadTriggered] = useState(false)

  const handleUpload = async () => {
    setUploadTriggered(true)
    const files = Array.from(fileInput.current?.files || [])
    if (!files || typeof uploadAssets !== 'function') {
      return
    }

    const toastId = toast(UploadProgressToast, {
      type: 'success',
      toastId: 'asset-upload-toast',
      closeOnClick: true,
      autoClose: 4000,
    })

    // Upload in parallel, then update assets
    const assets = await uploadAssets(files)
    const validAssets = assets.filter(
      (asset): asset is AssetAttributes => asset?.assetUuid != null
    )

    for (const asset of validAssets) {
      // Wait for next tick so the asset store gets updated
      await new Promise((res) => setTimeout(res))
      editor.commands.insertAsset(asset)
    }

    if (validAssets.length !== assets.length) {
      toast.update(toastId, {
        render: (props) => (
          <UploadFailedToast
            {...props}
            invalid={assets.length - validAssets.length}
            total={assets.length}
          />
        ),
        type: 'error',
        closeOnClick: true,
        autoClose: 5000,
      })
    } else {
      toast.dismiss(toastId)
    }

    setUploadTriggered(false)
  }

  if (!editor) {
    return null
  }

  return (
    <StrictMode>
      <MemoizedPopover label={!uploadTriggered ? labelText : ''}>
        <button
          css={styleOverrides}
          {...buttonProps}
          onClick={() => fileInput.current?.click()}
          aria-label={labelText}
        >
          {icon}
        </button>
      </MemoizedPopover>
      <input
        accept={allowedFileTypes}
        id="upload-board-cover-image"
        onChange={handleUpload}
        ref={fileInput}
        style={{ display: 'none' }}
        aria-hidden={true}
        type="file"
        multiple
      />
    </StrictMode>
  )
}
